/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : DocumentLibraryIndex.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import ProjectvariantSearchbar from "../../../components/customComponentsV2/common/projectSearchbar/ProjectvariantSearchbar";
import ProjectVariantDialog from "../../../components/customComponentsV2/vlc/ProjectVarientAccordian/ProjectVariantDialog";
import CommonBreadcrumbs from "../../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import CustomSearchbarWithApiV2 from "../../../components/customComponentsV2/common/searchBar/CustomSearchbarWithApiV2";
import documentLibraryAction from "../../../redux/actions/documentLibraryAction";
import DocumentLibraryCard from "./DocumentLibraryCard";
import ViewDynammicFormDetails from "../../admin-settings/DynamicForms/ViewDynammicFormDetails";
import dynamicTemplateAction from "../../../redux/actions/dynamicTemplateAction";
import { HandleApiActions } from "../../../redux/actions/actionHandler";
import UpdateDynamicForm from "../../admin-settings/DynamicForms/UpdateDynamicForm";
import CustomPaginationV2 from "../../../components/customComponentsV2/common/pagination/CustomPaginationV2";
import SortTemplate from "../../../components/customComponentsV2/common/Templates/SortTemplate";
import CustomFilterV2 from "../../../components/customComponentsV2/common/filter/CustomFilterV2";

import FilterTemplate from "../../../components/customComponentsV2/common/Templates/FilterTemplate";
import PreviewModal from "../../admin-settings/DynamicFormV2/PreviewModal";

function DocumentLibraryIndex() {
  const dispatch = useDispatch();
  const selectedProjectDetails = useSelector(
    (state) => state.projectConfigReducer.selectedProjectDetails,
  );
  const selectedVariantDetails = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails,
  );
  const getFormDetails = useSelector((state) => state.dynamicTemplateReducer);

  const [documentList, setDocumentList] = useState([]);
  const [singleFormDetails, setSingleFormDetails] = useState({});
  const [updateFormDetails, setUpdateFormDetails] = useState({});
  const [updateForm, setUpdateForm] = useState(false);
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedSortObj, setSelectedSortObj] = useState({
    sortBy: "created_at",
    sortOrder: "DESC",
  });
  const [selectedSortObjBeforeClick, setSelectedSortObjBeforeClick] = useState({
    sortBy: "created_at",
    sortOrder: "DESC",
  });
  const [anchorFilter, setAnchorFilter] = useState(null);
  const [openFilter, setOpenFilter] = useState(false);
  const [openNewFormView, setOpenNewFormView] = useState(false);
  const [selectedTypeFilter, setSelectedTypeFilter] = useState([
    { label: "Vehicle Systems", value: 400 },
    { label: "Vehicle Components", value: 300 },
    { label: "EE Components", value: 200 },
    { label: "Software Modules", value: 100 },
  ]);
  const [selectedTypeFilterBeforeClick, setSelectedTypeFilterBeforeClick] =
    useState([
      { label: "Vehicle Systems", value: 400 },
      { label: "Vehicle Components", value: 300 },
      { label: "EE Components", value: 200 },
      { label: "Software Modules", value: 100 },
    ]);

  const handleOpen = (dataId) => {

    setSingleFormDetails(dataId);

    if (dataId?.template_tag === "NEW") {
      setOpenNewFormView(true);
    } else if (dataId?.template_tag === "OLD") {
      setOpen(true);
    }
  };


  const handleFilterIconClick = (event) => {
    setAnchorFilter(event.currentTarget);
    setOpenFilter(true);
  };

  const handleFilterIconClose = () => {
    setAnchorFilter(null);
    setOpenFilter(false);
  };

  const handleApplyFilters = async (action) => {
    switch (action) {
      case "discard":
        setSelectedSortObjBeforeClick({
          sortBy: "created_at",
          sortOrder: "DESC",
        });
        setSelectedTypeFilterBeforeClick([
          { label: "Vehicle Systems", value: 400 },
          { label: "Vehicle Components", value: 300 },
          { label: "EE Components", value: 200 },
          { label: "Software Modules", value: 100 },
        ]);
        break;

      case "cancel":
        handleFilterIconClose();
        break;

      case "apply":
        setSelectedSortObj(selectedSortObjBeforeClick);
        setSelectedTypeFilter(selectedTypeFilterBeforeClick);
        setPage(1);
        handleFilterIconClose();
        break;

      default:
        break;
    }
  };
  // Close the modals
  const handleClose = (index) => {
    if (index === 1) {
      setOpen(false);
    } else if (index === 2) {
      setUpdateForm(false);
    } else if (index === 3) {
      setOpenNewFormView(false);
    }
  };

  useEffect(() => {
    dispatch(
      HandleApiActions({
        ...dynamicTemplateAction.get_d_template,
        params: {
          page_number: page,
          page_size: rowsPerPage,
        },
      }),
    );
  }, [open, updateForm, page, rowsPerPage]);
  return (
    <Box sx={{ p: 1 }}>
      <Grid container>
        <Grid item sm={12} sx={{ p: 1 }}>
          <CommonBreadcrumbs
            items={[{ label: "Document Library" }, { label: "Reports" }]}
          />
        </Grid>
        <Grid item sm={12}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <CustomFilterV2
              anchorEl={anchorFilter}
              open={openFilter}
              widthPerColumn="250px"
              handleClick={handleFilterIconClick}
              handleClose={handleFilterIconClose}
              handleFilterButton={handleApplyFilters}
              showClearAll={true}
              handleClearAll={() => handleApplyFilters("discard")}
              sections={[
                {
                  heading: "Sorting",
                  content: (
                    <SortTemplate
                      defaultValue={selectedSortObjBeforeClick}
                      handleOnChange={(sortValue) => {
                        setSelectedSortObjBeforeClick(sortValue);
                      }}
                    />
                  ),
                },
                {
                  heading: "Filter by Type",
                  content: (
                    <FilterTemplate
                      data={[
                        {
                          label: "Software Module",
                          value: 100,
                        },
                        {
                          label: "EE Component",
                          value: 200,
                        },
                        {
                          label: "Vehicle Components",
                          value: 300,
                        },
                        {
                          label: "Vehicle Systems",
                          value: 400,
                        },
                      ]}
                      handleOnChange={(value) => {
                        setSelectedTypeFilterBeforeClick(value);
                      }}
                      defaultValue={selectedTypeFilterBeforeClick}
                    />
                  ),
                },
              ]}
            />
            <Box>
              <ProjectvariantSearchbar />
            </Box>

            <ProjectVariantDialog />
          </Box>
        </Grid>
        <Grid item sm={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Box sx={{ width: "300px" }}>
            <CustomSearchbarWithApiV2
              apiAction={documentLibraryAction.get_df_reports}
              getDataOnSearch={(data, resp) => {
                setDocumentList(data);
                setTotalCount(resp?.total_count);
              }}
              placeholder={"Search Document Name"}
              searchKey={"name"}
              params={{
                project_id: parseInt(selectedProjectDetails?.project_id),
                variant_id: parseInt(selectedVariantDetails?.variant_id),
                is_ticket_required: true,
                page_number: page,
                page_size: rowsPerPage,
                [selectedSortObj.sortBy]: selectedSortObj.sortOrder,
                type: selectedTypeFilter.map((item) => item.value).join(","),
              }}
            />
          </Box>
        </Grid>
        <Grid
          container
          spacing={4}
          sx={{
            p: 1,
            ml: 2,
            mr: 2,
            mt: 2,
            height: "475px",
            overflow: "auto",
          }}
        >
          {documentList.length > 0 ? (
            documentList.map((item, index) => {
              function getTypeByValue(value) {
                const type = [
                  { label: "Vehicle Systems", value: 400 },
                  { label: "Vehicle Components", value: 300 },
                  { label: "EE Components", value: 200 },
                  { label: "Software Modules", value: 100 },
                ]?.find((item) => item.value === value);
                return type ? type.key : "Key not found";
              }
              return (
                <>
                  <Grid item xs={12} sm={6} md={6} lg={3} key={index}>
                    <DocumentLibraryCard
                      name={item.name}
                      moduleType={getTypeByValue(item.type)}
                      handleOpen={handleOpen}
                      data={item}
                    />
                  </Grid>
                </>
              );
            })
          ) : (
            <Grid item xs={12}>
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <Typography>No Data Found</Typography>
              </Box>
            </Grid>
          )}
        </Grid>
        <Grid item sm={12}>
          <CustomPaginationV2
            page={page}
            rowsPerPage={rowsPerPage}
            totalItems={totalCount}
            onPageChange={(newPage) => {
              setPage(newPage);
            }}
          />
        </Grid>
      </Grid>
      {open && (
        <ViewDynammicFormDetails
          data={singleFormDetails}
          open={open}
          handleClose={() => handleClose(1)}
        />
      )}
      {openNewFormView && (
        <PreviewModal
          isOpen={openNewFormView}
          onClose={() => handleClose(3)}
          pages={singleFormDetails?.fields || []}
          title={singleFormDetails?.name}
        />
      )}
      <UpdateDynamicForm
        data={updateFormDetails}
        open={updateForm}
        handleClose={() => handleClose(2)}
      />
    </Box>
  );
}

export default DocumentLibraryIndex;
